import React, { useEffect, useState, useRef } from "react";
import Modal from "react-bootstrap/Modal";
import { ValidatorForm } from "react-material-ui-form-validator";
import axios from "../../../../axios-order";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import SelectField from "../../../../components/ui/basicSelectInput";
import BoxWrapper from "../../../../components/ui/boxWrapper";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { canRead, canWrite } from "../../../../utils/privilegeUtils";
import excelIconImage from "../../../../asserts/images/excelicon.svg";

// ── Design tokens (identical palette to ItcAvailReport) ──────────────────────
const C = {
    navy: "#1a3f6f",
    accent: "#1a6fc4",
    accentLight: "#e8f1fb",
    white: "#ffffff",
    surface: "#f7f9fc",
    border: "#dde3ec",
    borderLight: "#eef1f6",
    text: "#1a3f6f",
    textSub: "#4a6080",
    textMuted: "#8298b2",
    success: { bg: "#edf8f1", text: "#1a6e3e", dot: "#28a55f" },
    warning: { bg: "#fff8e6", text: "#7a4e00", dot: "#f0a500" },
    error: { bg: "#fef2f2", text: "#991b1b", dot: "#ef4444" },
};

const monthArray = [
    { id: 4, value: "April" },
    { id: 5, value: "May" },
    { id: 6, value: "June" },
    { id: 7, value: "July" },
    { id: 8, value: "August" },
    { id: 9, value: "September" },
    { id: 10, value: "October" },
    { id: 11, value: "November" },
    { id: 12, value: "December" },
    { id: 1, value: "January" },
    { id: 2, value: "February" },
    { id: 3, value: "March" },
];

function formatDateTime(dt) {
    if (!dt) return "—";
    const cleaned = dt.replace("Z", "");
    const parts = cleaned.includes("T") ? cleaned.split("T") : cleaned.split(" ");
    const date = parts[0];
    const time = parts[1] || "";
    return (
        <span>
            <span style={{ color: C.text, fontWeight: "500" }}>{date}</span>
            {time && <span style={{ color: C.textMuted }}> · {time}</span>}
        </span>
    );
}

const DocIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="9" y1="13" x2="15" y2="13" />
        <line x1="9" y1="17" x2="13" y2="17" />
    </svg>
);

// ── Component ─────────────────────────────────────────────────────────────────
export default function Gtsr9OutwardComparison() {
    const history = useHistory();
    const formRef = useRef(null);

    const [newReconFormOpen, setNewReconFormOpen] = useState(false);
    const [newReconFormData, setNewReconFormData] = useState({ business_entity_id: localStorage.getItem("business_entity_id") });
    const [newReconHistoryTable, setNewReconHistoryTable] = useState([]);
    const [yearFromArr, setFromYearArr] = useState([]);
    const [monthsArrau, setMonthsArray] = useState(monthArray);
    const [errorMessageForBoxWrapper, setErrorMessageForBoxWrapper] = useState("");
    const [closeBoxWrapper, setCloseBoxWrapper] = useState(false);
    const [loaderForTable, setLoaderForTable] = useState(true);
    const [selectedYear, setSelectedYear] = useState("");
    const [selectedFromMonth, setSelectedFromMonth] = useState("");
    const [selectedToMonth, setSelectedToMonth] = useState("");
    const [selectedComparisonType, setSelectedComparisonType] = useState("");
    const [hoveredRow, setHoveredRow] = useState(null);

    const [excelLoadingId, setExcelLoadingId] = useState(null);

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleBack = () => history.push("/reportCards");
    const handleComparisonTypeChange = (e) => { console.log(e.target); setSelectedComparisonType(e.target.value); };
    const handleYearChange = (e) => setSelectedYear(e.target.value);
    const handleFromMonthChange = (e) => setSelectedFromMonth(e.target.value);
    const handleToMonthChange = (e) => setSelectedToMonth(e.target.value);

    // ── Data fetch ────────────────────────────────────────────────────────────
    const getTdsReconDetails = () => {
        axios
            .get(`/gstr9_comparison_summary/${localStorage.getItem("business_entity_id")}/Outward Comparison`)
            .then((res) => {
                setLoaderForTable(false);
                setNewReconHistoryTable(res.data.records);
                setFromYearValues(res.data.financial_years);
            })
            .catch((error) => {
                setLoaderForTable(false);
                toast.error(error?.response?.data?.message, { autoClose: 3000, progress: 0 });
            });
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const payloadData = {
            financial_year: selectedYear,
            from_month: selectedFromMonth,
            to_month: selectedToMonth,
            type1: "Outward Comparison",
            type2: selectedComparisonType,
            business_entity_id: localStorage.getItem("business_entity_id"),
        };
        axios.post("/gstr9_comparison", payloadData)
            .then(() => getTdsReconDetails())
            .catch((err) => toast.error(err?.response?.data?.errors, { autoClose: 3000, progress: 0 }));
        newReconFormModalClose();
    };

    useEffect(() => { getTdsReconDetails(); setFromYearValues(); }, []);

    const newReconFormModalOpen = () => {
        setNewReconFormOpen(true);
        setSelectedYear("");
        setSelectedFromMonth("");
        setSelectedToMonth("");
        setSelectedComparisonType("");
    };

    const newReconFormModalClose = () => {
        setNewReconFormOpen(false);
        setNewReconFormData({ business_entity_id: localStorage.getItem("business_entity_id") });
    };

    const downloadBase64File = (contentType, base64Data, fileName) => {
        const linkSource = `data:${contentType};base64,${base64Data}`;
        const downloadLink = document.createElement("a");
        downloadLink.href = linkSource;
        downloadLink.download = fileName;
        downloadLink.click();
    };

    const genarateExcel = (item) => {
        setExcelLoadingId(item.id);
        const contentType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        const filename = `${item.comparison_type2}.xlsx`;
        axios.get(`/gstr9_comparison_report/${item.id}`)
            .then((res) => downloadBase64File(contentType, res.data.file, filename))
            .catch(() => { })
            .finally(() => setExcelLoadingId(null));
    };

    const viewReportData = (item) => {
        history.push({ pathname: `/OutwardSummaryDetails`, state: { reportId: item.id } });
    };

    const setFromYearValues = () => {
        let current_year = new Date().getFullYear();
        let yearArr = [];
        for (let i = current_year - 10; i <= current_year; i++) {
            const toYear = String(i + 1);
            yearArr.push({ id: i, year: `${i}-${toYear.slice(2)}` });
        }
        setFromYearArr(yearArr);
    };

    const refreshUploadHistoryTable = () => getTdsReconDetails();
    const FunForcloseBoxWapper = () => setCloseBoxWrapper(false);

    // ── Status badge ──────────────────────────────────────────────────────────
    const StatusBadge = ({ status }) => {
        const isPass = status === "Pass";
        const isFail = status === "Fail";
        const scheme = isPass ? C.success : isFail ? C.error : C.warning;
        return (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", padding: "3px 10px", borderRadius: "5px", fontSize: "11px", fontWeight: "600", background: scheme.bg, color: scheme.text }}>
                <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: scheme.dot, flexShrink: 0 }} />
                {status || "—"}
            </span>
        );
    };

    // ── Styles ────────────────────────────────────────────────────────────────
    const thStyle = {
        padding: "11px 14px", textAlign: "left", fontSize: "11px", fontWeight: "600",
        letterSpacing: "0.05em", color: "#ffffff", background: C.navy,
        borderBottom: "1px solid #16355c", textTransform: "uppercase", whiteSpace: "nowrap",
    };
    const tdStyle = {
        padding: "8px 14px", fontSize: "13px", color: C.text,
        borderBottom: `1px solid ${C.borderLight}`,
    };
    const labelStyle = {
        fontSize: "12px", fontWeight: "600", color: C.textSub,
        marginBottom: "6px", display: "block",
        textTransform: "uppercase", letterSpacing: "0.05em",
    };

    // ── Table body renderer ───────────────────────────────────────────────────
    const renderTableBody = () => {
        if (loaderForTable) {
            return Array.from({ length: 3 }).map((_, i) => (
                <tr key={i}>
                    {[10, 10, 10, 20, 15, 10, 8, 8].map((w, j) => (
                        <td key={j} style={tdStyle}>
                            <div style={{ height: "14px", borderRadius: "4px", background: C.borderLight, width: `${w}%`, animation: "pulse 1.4s ease-in-out infinite" }} />
                        </td>
                    ))}
                </tr>
            ));
        }
        if (!newReconHistoryTable || newReconHistoryTable.length === 0) {
            return (
                <tr>
                    <td colSpan={8} style={{ ...tdStyle, textAlign: "center", padding: "52px 20px" }}>
                        <div style={{ fontSize: "28px", marginBottom: "10px", opacity: 0.4 }}>📄</div>
                        <div style={{ fontWeight: "500", marginBottom: "4px", color: C.textSub }}>No records found</div>
                        <div style={{ fontSize: "12px", color: C.textMuted }}>Generate your first Outward Comparison to get started.</div>
                    </td>
                </tr>
            );
        }
        return newReconHistoryTable.map((item, index) => (
            <tr
                key={index}
                onMouseEnter={() => setHoveredRow(index)}
                onMouseLeave={() => setHoveredRow(null)}
                style={{ background: hoveredRow === index ? C.surface : C.white, transition: "background 0.1s" }}
            >
                <td style={tdStyle}><span style={{ fontWeight: "600" }}>{item.financial_year}</span></td>
                <td style={tdStyle}><span style={{ fontWeight: "600" }}>{item.from_month}</span></td>
                <td style={tdStyle}><span style={{ fontWeight: "600" }}>{item.to_month}</span></td>
                <td style={tdStyle}>
                    <span style={{ fontSize: "12px", color: C.textMuted, fontVariantNumeric: "tabular-nums" }}>
                        {formatDateTime(item.run_date_time)}
                    </span>
                </td>
                <td style={tdStyle}>
                    <span style={{ fontSize: "12px", color: C.textSub, fontWeight: "500" }}>{item.comparison_type2}</span>
                </td>
                <td style={tdStyle}><StatusBadge status={item.comparison_status} /></td>
                {/* Report / View */}
                <td style={{ ...tdStyle, textAlign: "center" }}>
                    <button
                        onClick={() => viewReportData(item)}
                        style={{ padding: "4px 12px", borderRadius: "6px", whiteSpace: "nowrap", border: `1px solid ${hoveredRow === index ? C.accent : C.border}`, background: hoveredRow === index ? C.accentLight : C.white, color: hoveredRow === index ? C.accent : C.textSub, fontSize: "12px", fontWeight: "500", cursor: "pointer", transition: "all 0.15s" }}
                    >
                        View →
                    </button>
                </td>
                {/* Excel download */}
                {/* Excel download */}
                <td style={{ ...tdStyle, textAlign: "center" }}>
                    <button
                        onClick={() => item.comparison_status === "Pass" && genarateExcel(item)}
                        title={item.comparison_status === "Pass" ? "Download Excel" : "Not available"}
                        disabled={excelLoadingId === item.id}
                        style={{ background: "none", border: "none", cursor: item.comparison_status === "Pass" ? "pointer" : "default", padding: "2px", borderRadius: "4px", opacity: item.comparison_status === "Pass" ? 1 : 0.35, display: "inline-flex", alignItems: "center" }}
                    >
                        {excelLoadingId === item.id ? (
                            <div
                                style={{
                                    width: "16px",
                                    height: "16px",
                                    border: `2px solid ${C.border}`,
                                    borderTopColor: C.accent,
                                    borderRadius: "50%",
                                    animation: "spin 0.7s linear infinite",
                                }}
                            />
                        ) : (
                            <img src={excelIconImage} style={{ height: "23px", width: "25px" }} alt="Excel" />
                        )}
                    </button>
                </td>
            </tr>
        ));
    };

    const totalCount = newReconHistoryTable ? newReconHistoryTable.length : 0;
    const passCount = newReconHistoryTable ? newReconHistoryTable.filter(r => r.comparison_status === "Pass").length : 0;
    const fyCount = newReconHistoryTable ? new Set(newReconHistoryTable.map(r => r.financial_year)).size : 0;

    return (
        <>
            <style>{`
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
  @keyframes spin { to { transform: rotate(360deg); } }
`}</style>

            <div style={{ padding: "28px 32px", background: C.surface, fontFamily: "inherit" }}>

                {/* ── Page header ── */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: C.white, border: `1px solid ${C.border}`, borderRadius: "10px", padding: "16px 20px", marginBottom: "20px", boxShadow: "0 1px 3px rgba(13,33,55,0.06)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                        <button
                            onClick={handleBack}
                            title="Back to Reports"
                            style={{ width: "32px", height: "32px", borderRadius: "8px", border: `1px solid ${C.border}`, background: C.white, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: C.textSub, fontSize: "14px", flexShrink: 0 }}
                        >←</button>
                        <div style={{ width: "42px", height: "42px", borderRadius: "10px", background: C.accentLight, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <DocIcon />
                        </div>
                        <div>
                            <div style={{ fontSize: "23px", fontWeight: "700", color: C.text, lineHeight: 1.2 }}>Outward Comparison</div>
                            <div style={{ fontSize: "12px", color: C.textMuted, marginTop: "4px" }}>GSTR-9 outward comparison records</div>
                        </div>
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                        <button
                            onClick={refreshUploadHistoryTable}
                            style={{ display: "flex", alignItems: "center", gap: "6px", padding: "0 16px", height: "36px", borderRadius: "8px", background: C.white, color: C.textSub, border: `1px solid ${C.border}`, fontSize: "13px", fontWeight: "500", cursor: "pointer" }}
                        >
                            ↻ Refresh
                        </button>
                       {canWrite("GST OUTWARD COMPARISON") && (
                        <button
                            onClick={newReconFormModalOpen}
                            style={{ display: "flex", alignItems: "center", gap: "6px", padding: "0 18px", height: "36px", borderRadius: "8px", background: C.navy, color: C.white, border: "none", fontSize: "13px", fontWeight: "500", cursor: "pointer" }}
                        >
                            <span style={{ fontSize: "16px", lineHeight: 1 }}>+</span>
                            Outward Comparison
                        </button>
                        )}
                    </div>
                </div>

                {/* ── Metrics cards ── */}
                {/* <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "14px", marginBottom: "20px" }}>
                    {[
                        { label: "Total Records",   value: totalCount, borderColor: C.accent,  badge: totalCount > 0 },
                        { label: "Pass",            value: passCount,  borderColor: "#28a55f", badge: false },
                        { label: "Financial Years", value: fyCount,    borderColor: "#8298b2", badge: false },
                    ].map(({ label, value, borderColor, badge }) => (
                        <div key={label} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: "10px", padding: "16px 20px", boxShadow: "0 1px 3px rgba(13,33,55,0.05)", borderTop: `3px solid ${borderColor}` }}>
                            <div style={{ fontSize: "26px", fontWeight: "700", color: C.text, lineHeight: 1, letterSpacing: "-0.5px" }}>{value}</div>
                            <div style={{ fontSize: "11px", color: C.textMuted, marginTop: "5px", fontWeight: "500", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
                            {badge && <div style={{ display: "inline-block", marginTop: "8px", fontSize: "10px", padding: "2px 8px", borderRadius: "12px", background: C.success.bg, color: C.success.text, fontWeight: "600" }}>All generated</div>}
                        </div>
                    ))}
                </div> */}

                {/* ── Table card ── */}
                <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: "10px", overflow: "hidden", boxShadow: "0 1px 3px rgba(13,33,55,0.05)" }}>
                    <div style={{ maxHeight: "480px", overflowY: "auto", overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", minWidth: "900px" }}>
                            <colgroup>
                                <col style={{ width: "10%" }} />
                                <col style={{ width: "10%" }} />
                                <col style={{ width: "10%" }} />
                                <col style={{ width: "20%" }} />
                                <col style={{ width: "18%" }} />
                                <col style={{ width: "12%" }} />
                                <col style={{ width: "10%" }} />
                                <col style={{ width: "10%" }} />
                            </colgroup>
                            <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                                <tr>
                                    <th style={thStyle}>Fiscal Year</th>
                                    <th style={thStyle}>From Month</th>
                                    <th style={thStyle}>To Month</th>
                                    <th style={thStyle}>Created Date</th>
                                    <th style={thStyle}>Comparison Type</th>
                                    <th style={thStyle}>Status</th>
                                    <th style={{ ...thStyle, textAlign: "center" }}>Report</th>
                                    <th style={{ ...thStyle, textAlign: "center" }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>{renderTableBody()}</tbody>
                        </table>
                    </div>

                    {/* Footer */}
                    {!loaderForTable && newReconHistoryTable && newReconHistoryTable.length > 0 && (
                        <div style={{ padding: "10px 18px", borderTop: `1px solid ${C.borderLight}`, fontSize: "11px", color: C.textMuted, background: C.white }}>
                            Showing {newReconHistoryTable.length} record{newReconHistoryTable.length !== 1 ? "s" : ""}
                        </div>
                    )}
                </div>

            </div>

            {/* ── Error BoxWrapper (unchanged) ── */}
            {closeBoxWrapper && (
                <BoxWrapper
                    boxTitle="Error"
                    cutIcon={true}
                    children={errorMessageForBoxWrapper}
                    closeBoxWapper={FunForcloseBoxWapper}
                    colorForMessage="red"
                    ErrorIcon={<ErrorOutlineOutlinedIcon />}
                />
            )}

           {/* ── Modal ── */}
            {canWrite("GST OUTWARD COMPARISON") && <Modal size="lg" centered show={newReconFormOpen} onHide={newReconFormModalClose}>
                <Modal.Header closeButton style={{ background: C.navy, borderBottom: "none", padding: "16px 22px" }}>
                    <Modal.Title style={{ fontSize: "18px", fontWeight: "600", color: "#ffffff" }}>
                        Outward Comparison
                    </Modal.Title>
                </Modal.Header>

                <ValidatorForm onSubmit={handleSubmit} onError={(errors) => console.log(errors)}>
                    <Modal.Body style={{ padding: "22px 22px 10px" }}>

                        {/* Financial Year */}
                        <div style={{ marginBottom: "16px" }}>
                            <label style={labelStyle}>Select Financial Year <span style={{ color: "#e53e3e" }}>*</span></label>
                            <SelectField
                                validator={["required"]}
                                errorMsg={["This field is required"]}
                                choice_id="year"
                                choice_name="year"
                                name="year"
                                choices={yearFromArr}
                                value={selectedYear}
                                onChange={handleYearChange}
                            />
                        </div>

                        {/* From Month + To Month */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
                            <div>
                                <label style={labelStyle}>From Month <span style={{ color: "#e53e3e" }}>*</span></label>
                                <SelectField
                                    validator={["required"]}
                                    errorMsg={["This field is required"]}
                                    choice_id="value"
                                    choice_name="value"
                                    name="from_month"
                                    value={selectedFromMonth}
                                    onChange={handleFromMonthChange}
                                    choices={monthsArrau}
                                />
                            </div>
                            <div>
                                <label style={labelStyle}>To Month <span style={{ color: "#e53e3e" }}>*</span></label>
                                <SelectField
                                    validator={["required"]}
                                    errorMsg={["This field is required"]}
                                    choice_id="value"
                                    choice_name="value"
                                    name="to_month"
                                    value={selectedToMonth}
                                    onChange={handleToMonthChange}
                                    choices={monthsArrau}
                                />
                            </div>
                        </div>

                        {/* Comparison Type */}
                        <div style={{ marginBottom: "16px" }}>
                            <label style={labelStyle}>Comparison Type <span style={{ color: "#e53e3e" }}>*</span></label>
                            <SelectField
                                choice_id="type"
                                choice_name="type"
                                validator={["required"]}
                                errorMsg={["This field is required"]}
                                choices={[
                                    { id: 0, type: "Books vs GSTR1" },
                                    { id: 1, type: "Books vs GSTR3B" },
                                    { id: 5, type: "GSTR1 vs GSTR3B" },
                                    { id: 6, type: "Books vs GSTR1 vs GSTR3B" },
                                ]}
                                value={selectedComparisonType}
                                onChange={handleComparisonTypeChange}
                            />
                        </div>

                    </Modal.Body>

                    <Modal.Footer style={{ padding: "12px 22px", gap: "8px", borderTop: `1px solid ${C.borderLight}` }}>
                        <button
                            type="button"
                            onClick={newReconFormModalClose}
                            style={{ padding: "0 18px", height: "36px", borderRadius: "8px", border: `1px solid ${C.border}`, background: C.white, fontSize: "13px", color: C.textSub, cursor: "pointer" }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            style={{ padding: "0 22px", height: "36px", borderRadius: "8px", background: C.navy, color: C.white, border: "none", fontSize: "13px", fontWeight: "600", cursor: "pointer" }}
                        >
                            Submit
                        </button>
                    </Modal.Footer>
                </ValidatorForm>
            </Modal>}
        </>
    );
}